/**
 * Phone: everything about an Android phone that is not its spec sheet - what Google last reported,
 * its policy, the commands, lost mode with the locations it reported, its apps, and ending management.
 *
 *   * EVERY COMMAND IS "SENT", NEVER "DONE". Google queues it and the phone carries it out the next
 *     time it is online; the outcome arrives later, as a History row.
 *   * WHAT A PHONE ALLOWS DEPENDS ON WHO OWNS IT. Restart is fully managed only; lost mode is company
 *     phones only. The buttons that cannot work are not offered, and the reason is said once.
 *   * THE PASSCODE IS NEVER KEPT. It goes to Google in the request and is cleared from the box the
 *     moment the request returns, whatever it answered.
 *   * REMOVING MANAGEMENT IS TYPED, like a customer purge: on a company phone it is a factory reset.
 */
import * as crm from '../../api/crm.js';
import * as auth from '../../api/auth.js';
import { describeError } from '../../api/http.js';
import * as mobile from '../../api/mobile.js';
import {
  MobileCommandKind,
  MobileOwnership,
  type InstalledSoftwareDto,
  type MobileCommandRequest,
  type MobileLocationDto,
  type MobilePolicyDto
} from '../../contracts.gen.js';
import { join, localDateTime, plural } from '../../format.js';
import { CardList } from '../../ui/CardList.js';
import { chip } from '../../ui/Chip.js';
import { confirm } from '../../ui/dialogs.js';
import { link } from '../../ui/Link.js';
import { StatusLine } from '../../ui/StatusLine.js';
import {
  button,
  buttonRow,
  card,
  checkLine,
  comboBox,
  field,
  fieldRow,
  FONT,
  GAP,
  h2,
  inlineRow,
  muted,
  passwordBox,
  stackOf,
  textBox,
  value,
  vstack,
  wrappingCheck
} from '../../ui/layout.js';
import { OWNERSHIP_LABELS } from '../sections/android-phones.js';
import type { DeviceTab, DeviceTabContext } from './context.js';
import { insets } from 'webwin';

export function phoneTab(dc: DeviceTabContext): DeviceTab {
  const panel = vstack({ spacing: GAP.md, hAlign: 'stretch', autoWidth: false });
  const isAdmin = auth.isAdmin();
  let policies: MobilePolicyDto[] = [];
  let policiesFor = 0;
  let appsLoaded = false;

  // ---- State ----
  const chips = inlineRow();
  const stateLine = muted('');
  const facts = muted('');
  const compliance = vstack({ spacing: GAP.xs });
  const publishError = muted('', 'warn');
  const refreshButton = button('Read from Google now', () => void refresh());
  const stateCard = card();
  stateCard.controls.add(stackOf(vstack({ spacing: GAP.sm }), chips, stateLine, facts, compliance, publishError, stackOf(buttonRow(), refreshButton)));

  // ---- Policy ----
  const policyPicker = comboBox<string>([], {});
  const assignButton = button('Move to this policy', () => void assign());
  const republishButton = button('Republish', () => void republish());
  const policyStatus = new StatusLine();
  const policyCard = card();
  policyCard.controls.add(
    stackOf(
      vstack({ spacing: GAP.sm }),
      value('Policy', { font: FONT.bold }),
      muted("The phone follows its own copy of this policy, filled in with its primary employee's email address. Republish after changing the employee."),
      field('Policy', policyPicker),
      stackOf(buttonRow(), assignButton, republishButton),
      policyStatus.label
    )
  );

  // ---- Commands ----
  const lockButton = button('Lock now', () => void send(command(MobileCommandKind.Lock), 'Lock the phone now?'));
  const rebootButton = button('Restart', () => void send(command(MobileCommandKind.Reboot), 'Restart the phone? Unsaved work on it may be lost.'));
  const passcodeButton = button('Set a new passcode…', () => togglePasscode());
  const ownershipNote = muted('');
  const commandStatus = new StatusLine();

  const passcode = passwordBox({ placeholder: 'At least 4 characters' });
  const passcodeAgain = passwordBox();
  const passcodePanel = card({ tint: 'alt', visible: false });
  passcodePanel.controls.add(
    stackOf(
      vstack({ spacing: GAP.sm }),
      muted('Replaces the screen lock (the work-profile lock on a personal phone) and locks the phone. It must meet the policy\'s passcode rule. Tell the person the new passcode yourself - it is not stored anywhere.'),
      fieldRow(field('New passcode', passcode), field('Again', passcodeAgain)),
      stackOf(buttonRow(), button('Set passcode', () => void setPasscode(), true), button('Cancel', () => togglePasscode(false)))
    )
  );

  const commandsCard = card();
  commandsCard.controls.add(
    stackOf(
      vstack({ spacing: GAP.sm }),
      value('Actions', { font: FONT.bold }),
      ownershipNote,
      stackOf(buttonRow(), lockButton, rebootButton, passcodeButton),
      passcodePanel,
      commandStatus.label
    )
  );

  // ---- Lost mode ----
  const lostLine = muted('');
  const lostMessage = textBox({ placeholder: 'This phone is lost. Please call the number below.' });
  const lostPhone = textBox({ placeholder: 'A number the finder can call' });
  const lostEmail = textBox({ placeholder: 'Or an email address' });
  const lostOrg = textBox({ placeholder: 'Optional - your company name' });
  const lostAddress = textBox({ placeholder: 'Optional - where to return it' });
  const startLostButton = button('Put in lost mode', () => void startLost(), true);
  const stopLostButton = button('Take out of lost mode', () => void stopLost());
  const lostForm = vstack({ spacing: GAP.sm });
  lostForm.controls.addRange([
    field('Message on the lock screen', lostMessage),
    fieldRow(field('Phone number', lostPhone), field('Email address', lostEmail)),
    fieldRow(field('Organization', lostOrg), field('Return address', lostAddress)),
    stackOf(buttonRow(), startLostButton)
  ]);
  const stopLostRow = stackOf(buttonRow(), stopLostButton);
  const lostStatus = new StatusLine();
  const locationList = new CardList<MobileLocationDto>({ key: l => l.id, build: buildLocation, max: 50 });
  const lostCard = card();
  lostCard.controls.add(
    stackOf(
      vstack({ spacing: GAP.sm }),
      value('Lost mode', { font: FONT.bold }),
      lostLine,
      lostForm,
      stopLostRow,
      lostStatus.label,
      locationList.panel
    )
  );

  // ---- Apps ----
  const appsLine = muted('');
  const appList = new CardList<InstalledSoftwareDto>({ key: a => a.key, build: buildApp, max: 250 });
  const appsCard = card();
  appsCard.controls.add(stackOf(vstack({ spacing: GAP.sm }), value('Apps', { font: FONT.bold }), appsLine, appList.panel));

  // ---- Removal ----
  const removeNote = muted('');
  const confirmName = textBox();
  const wipeSd = wrappingCheck('Also erase the SD card');
  const keepFrp = wrappingCheck("Keep Google's reset protection (recommended for a lost or stolen phone)", true);
  const removeReason = textBox({ placeholder: 'Optional - shown on the phone' });
  const removeButton = button('Remove management', () => void removeManagement());
  const removeStatus = new StatusLine();
  const removeForm = vstack({ spacing: GAP.sm });
  removeForm.controls.addRange([
    field("Type the phone's name to confirm", confirmName),
    checkLine(wipeSd),
    checkLine(keepFrp),
    field('Reason', removeReason),
    stackOf(buttonRow(), removeButton)
  ]);
  const removeCard = card({ tint: 'danger' });
  removeCard.controls.add(stackOf(vstack({ spacing: GAP.sm }), value('End management', { font: FONT.bold }), removeNote, removeForm, removeStatus.label));

  panel.controls.addRange([h2('Phone'), stateCard, policyCard, commandsCard, lostCard, appsCard, removeCard]);

  // =================================================================

  function apply(): void {
    const d = dc.device();
    const m = d?.mobile;
    if (!d || !m) return;
    const owned = m.ownership;
    const company = owned !== MobileOwnership.PersonalWorkProfile;
    const removed = d.uninstallCompleted;
    const retired = d.archived || removed;

    chips.controls.clear();
    chips.controls.add(chip(OWNERSHIP_LABELS[owned] ?? 'Phone', 'info'));
    chips.controls.add(m.policyCompliant ? chip('applying its policy', 'ok') : chip('not applying its policy', 'warn'));
    if (m.lostModeSinceUtc) chips.controls.add(chip('lost mode', 'danger'));
    if (removed) chips.controls.add(chip('management removed', 'neutral'));

    stateLine.text = m.lastStatusReportUtc
      ? `Last reported ${localDateTime(m.lastStatusReportUtc)}${m.lastPolicySyncUtc ? ` · policy last applied ${localDateTime(m.lastPolicySyncUtc)}` : ''}`
      : 'Google has not had a report from this phone yet.';
    facts.text = join([
      m.securityPatchLevel ? `security patch ${m.securityPatchLevel}` : null,
      m.encrypted === true ? 'encrypted' : m.encrypted === false ? 'NOT encrypted' : null,
      m.batteryPercent !== null && m.batteryPercent !== undefined ? `battery ${m.batteryPercent}%` : null,
      m.carrier || null,
      m.phoneNumbers.length > 0 ? m.phoneNumbers.join(', ') : null,
      m.imei ? `IMEI ${m.imei}` : null
    ]);
    facts.visible = facts.text.length > 0;

    compliance.controls.clear();
    for (const line of m.nonCompliance) compliance.controls.add(muted(line, 'warn'));
    publishError.text = m.lastPublishError ?? '';
    publishError.visible = publishError.text.length > 0;

    // Commands
    ownershipNote.text =
      owned === MobileOwnership.PersonalWorkProfile
        ? "This is the employee's own phone: only its work profile is managed. It cannot be restarted or put in lost mode from here."
        : owned === MobileOwnership.CompanyOwnedWorkProfile
          ? 'A company phone with a personal side: it cannot be restarted from here.'
          : '';
    ownershipNote.visible = ownershipNote.text.length > 0;
    rebootButton.visible = owned === MobileOwnership.FullyManaged;
    for (const b of [lockButton, rebootButton, passcodeButton, assignButton, republishButton, refreshButton]) b.enabled = !retired;
    commandsCard.visible = !removed;
    policyCard.visible = !removed;

    // Lost mode
    lostCard.visible = company && !removed;
    const lost = m.lostModeSinceUtc !== null && m.lostModeSinceUtc !== undefined;
    lostForm.visible = !lost && !retired;
    stopLostRow.visible = lost;
    lostLine.text = lost
      ? `In lost mode since ${localDateTime(m.lostModeSinceUtc!)}. The phone shows your message and reports where it is; locations appear below as they arrive.`
      : 'Locks the phone to a message for whoever finds it and makes it report its location. Nothing about where a phone is is collected outside lost mode.';
    if (lost || locationList.count > 0) void loadLocations();

    // Removal
    removeCard.visible = !removed;
    removeForm.visible = isAdmin;
    removeNote.text = !isAdmin
      ? 'An administrator can end management of this phone.'
      : company
        ? 'Google factory-resets the phone: everything on it is erased, and it leaves the CRM. This cannot be undone.'
        : "Google removes the work profile and everything in it. The person's own apps and data are not touched. This cannot be undone.";
    wipeSd.visible = company;
    checkLine(wipeSd).visible = company;
    checkLine(keepFrp).visible = company;

    if (policiesFor !== d.customerId) void loadPolicies(d.customerId);
    else bindPolicyPicker();
    if (!appsLoaded) void loadApps();
    dc.relayout();
  }

  function bindPolicyPicker(): void {
    const current = dc.device()?.mobile?.policyId ?? null;
    policyPicker.items = policies.map(p => (p.isDefault ? `${p.name} (default)` : p.name));
    const index = policies.findIndex(p => p.id === current);
    policyPicker.selectedIndex = index >= 0 ? index : policies.length > 0 ? 0 : -1;
  }

  async function loadPolicies(customerId: number): Promise<void> {
    policiesFor = customerId;
    try {
      policies = await mobile.getMobilePolicies(customerId, dc.ctx.dispose.signal());
      if (dc.ctx.cancelled()) return;
      bindPolicyPicker();
      dc.relayout();
    } catch (error) {
      if (!dc.ctx.cancelled()) policyStatus.error('Could not load the policies: ' + describeError(error));
    }
  }

  async function loadApps(): Promise<void> {
    appsLoaded = true;
    try {
      const inventory = await crm.getSoftwareInventory(dc.deviceId, dc.ctx.dispose.signal());
      if (dc.ctx.cancelled()) return;
      const apps = [...(inventory?.software ?? [])].sort((a, b) => a.name.localeCompare(b.name));
      appList.set(apps);
      appsLine.text =
        apps.length === 0
          ? 'No apps reported yet.'
          : dc.device()?.mobile?.ownership === MobileOwnership.PersonalWorkProfile
            ? `${plural(apps.length, 'app')} in the work profile. The person's own apps are private and not reported.`
            : plural(apps.length, 'app') + (appList.overflow > 0 ? ` - the first ${appList.count} are shown.` : '');
      dc.relayout();
    } catch (error) {
      if (!dc.ctx.cancelled()) appsLine.text = 'Could not load the apps: ' + describeError(error);
    }
  }

  async function loadLocations(): Promise<void> {
    try {
      const list = await mobile.getMobileLocations(dc.deviceId, dc.ctx.dispose.signal());
      if (dc.ctx.cancelled()) return;
      locationList.set(list);
      dc.relayout();
    } catch {
      // The card still says what lost mode does; a failed read is retried on the next refresh.
    }
  }

  function buildApp(a: InstalledSoftwareDto) {
    const shell = card({ padding: insets(8, 6) });
    shell.controls.add(stackOf(vstack({ spacing: 0 }), value(join([a.name, a.version]), { font: FONT.small }), muted(a.publisher)));
    return shell;
  }

  function buildLocation(l: MobileLocationDto) {
    const shell = card({ padding: insets(10, 8) });
    const where = `${l.latitude.toFixed(5)}, ${l.longitude.toFixed(5)}`;
    shell.controls.add(
      stackOf(
        vstack({ spacing: GAP.xs }),
        value(localDateTime(l.whenUtc), { font: FONT.bold }),
        muted(join([where, l.accuracyMeters ? `within ${Math.round(l.accuracyMeters)} m` : null, l.batteryPercent !== null && l.batteryPercent !== undefined ? `battery ${l.batteryPercent}%` : null])),
        link('Open in OpenStreetMap ↗', () =>
          window.open(`https://www.openstreetmap.org/?mlat=${l.latitude}&mlon=${l.longitude}#map=17/${l.latitude}/${l.longitude}`, '_blank', 'noopener')
        )
      )
    );
    return shell;
  }

  // ---- Actions ----

  async function send(req: MobileCommandRequest, question: string, status = commandStatus): Promise<boolean> {
    const name = dc.device()?.name ?? 'this phone';
    if (!(await confirm(dc.ctx.desktop, { title: name, message: question }))) return false;
    status.busy('Sending to Google…');
    dc.relayout();
    const result = await mobile.sendMobileCommand(dc.deviceId, req);
    if (dc.ctx.cancelled()) return false;
    if (result.success) status.ok(result.message);
    else status.error(result.message);
    await dc.reload();
    return result.success;
  }

  function togglePasscode(show = !passcodePanel.visible): void {
    passcodePanel.visible = show;
    passcode.text = '';
    passcodeAgain.text = '';
    dc.relayout();
  }

  async function setPasscode(): Promise<void> {
    const value1 = passcode.text;
    if (value1.length < 4) {
      commandStatus.error('A passcode is at least 4 characters.');
      return;
    }
    if (value1 !== passcodeAgain.text) {
      commandStatus.error('The two passcodes do not match.');
      return;
    }
    const req = command(MobileCommandKind.ResetPasscode, { newPasscode: value1 });
    passcode.text = '';
    passcodeAgain.text = '';
    if (await send(req, 'Set this new passcode and lock the phone? Whoever has the phone needs it to unlock.')) togglePasscode(false);
  }

  async function startLost(): Promise<void> {
    const message = lostMessage.text.trim();
    if (!message) {
      lostStatus.error('Write the message the lock screen should show.');
      return;
    }
    if (!lostPhone.text.trim() && !lostEmail.text.trim()) {
      lostStatus.error('Give a phone number or an email address the finder can use.');
      return;
    }
    await send(
      command(MobileCommandKind.StartLostMode, {
        lostMessage: message,
        lostPhoneNumber: lostPhone.text.trim() || null,
        lostEmailAddress: lostEmail.text.trim() || null,
        lostOrganization: lostOrg.text.trim() || null,
        lostStreetAddress: lostAddress.text.trim() || null
      }),
      'Put the phone in lost mode? It locks to your message as soon as it is online and starts reporting where it is.',
      lostStatus
    );
  }

  async function stopLost(): Promise<void> {
    await send(command(MobileCommandKind.StopLostMode), 'Take the phone out of lost mode? Location reporting stops.', lostStatus);
  }

  async function assign(): Promise<void> {
    const policy = policies[policyPicker.selectedIndex];
    if (!policy) return;
    policyStatus.busy(`Moving to "${policy.name}"…`);
    try {
      await mobile.assignMobilePolicy(dc.deviceId, policy.id);
      if (dc.ctx.cancelled()) return;
      policyStatus.ok(`The phone follows "${policy.name}" now. It applies it the next time it is online.`);
      await dc.reload();
    } catch (error) {
      if (!dc.ctx.cancelled()) policyStatus.error(describeError(error));
    }
  }

  async function republish(): Promise<void> {
    policyStatus.busy('Republishing…');
    try {
      await mobile.republishMobilePolicy(dc.deviceId);
      if (dc.ctx.cancelled()) return;
      policyStatus.ok('Republished. The phone applies it the next time it is online.');
      await dc.reload();
    } catch (error) {
      if (!dc.ctx.cancelled()) policyStatus.error(describeError(error));
    }
  }

  async function refresh(): Promise<void> {
    refreshButton.enabled = false;
    try {
      await mobile.refreshMobileDevice(dc.deviceId);
      if (dc.ctx.cancelled()) return;
      appsLoaded = false;
      await dc.reload();
    } catch (error) {
      if (!dc.ctx.cancelled()) dc.status.error(describeError(error));
    } finally {
      refreshButton.enabled = true;
    }
  }

  async function removeManagement(): Promise<void> {
    const d = dc.device();
    if (!d) return;
    if (confirmName.text.trim() !== d.name.trim()) {
      removeStatus.error(`Type the phone's name exactly ("${d.name}") to confirm.`);
      return;
    }
    const company = d.mobile?.ownership !== MobileOwnership.PersonalWorkProfile;
    const question = company
      ? `Factory-reset ${d.name} and end its management? Everything on the phone is erased.`
      : `Remove the work profile from ${d.name}? Work apps and data on it are erased.`;
    if (!(await confirm(dc.ctx.desktop, { title: 'Remove management', message: question }))) return;
    removeButton.enabled = false;
    removeStatus.busy('Asking Google…');
    const result = await mobile.removeMobileManagement(dc.deviceId, {
      confirmName: confirmName.text.trim(),
      wipeExternalStorage: company && wipeSd.checked,
      preserveResetProtection: company && keepFrp.checked,
      reason: removeReason.text.trim() || null
    });
    removeButton.enabled = true;
    if (dc.ctx.cancelled()) return;
    if (result.success) {
      dc.status.ok(result.message);
      confirmName.text = '';
    } else {
      removeStatus.error(result.message);
    }
    await dc.reload();
  }

  return { panel, applyDevice: apply };
}

/** A command with every optional member present, as the generated contract requires. */
export function command(kind: MobileCommandKind, extra: Partial<MobileCommandRequest> = {}): MobileCommandRequest {
  return {
    kind,
    newPasscode: null,
    lostMessage: null,
    lostPhoneNumber: null,
    lostEmailAddress: null,
    lostStreetAddress: null,
    lostOrganization: null,
    ...extra
  };
}
